import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS, BASIC_LANGS } from '../data';
import { EditableSpan } from './EditableSpan';
import { useBuilderContext } from '../context/BuilderContext';
import { PreviewLangSwitcher } from './preview/PreviewLangSwitcher';
import { PreviewQuestionHeader } from './preview/PreviewQuestionHeader';
import { PreviewIndividualOptions } from './preview/PreviewIndividualOptions';
import { PreviewCollectiveOptions } from './preview/PreviewCollectiveOptions';
import { useGdprOuKey } from '@/hooks/useOUs';

interface PreviewPanelProps {
  onOpenIconPicker: (qIndex: number) => void;
}

export function PreviewPanel({ onOpenIconPicker }: PreviewPanelProps) {
  const { t } = useTranslation();
  const { form, setForm, enfoqueFilter } = useBuilderContext();
  const [previewLang, setPreviewLang] = useState('es');
  const [collapsedQs, setCollapsedQs] = useState<Set<number>>(new Set());

  const toggleQCollapse = (i: number) =>
    setCollapsedQs(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const gdprOuKey = useGdprOuKey();
  const isEuropa = !!gdprOuKey && form.audienceOUs.includes(gdprOuKey);
  const langs = isEuropa ? ALL_LANGS : BASIC_LANGS;
  const activeLang = (langs as readonly string[]).includes(previewLang) ? previewLang : langs[0];
  const isSpanish = activeLang === 'es';

  const hasData = !!(form.name || form.description || form.surveyType);
  const hasQuestions = form.questions.length > 0;

  const hasMixed = form.questions.some(q => q.enfoque === 'individual') &&
                   form.questions.some(q => q.enfoque === 'colectivo');
  const activeEnfoqueFilter = hasMixed ? enfoqueFilter : 'all';
  const visibleQuestions = activeEnfoqueFilter === 'all'
    ? form.questions
    : form.questions.filter(q => q.enfoque === activeEnfoqueFilter);

  const getQText = useCallback((qIndex: number) => {
    if (isSpanish) return form.questions[qIndex]?.text ?? '';
    return form.translations?.[activeLang]?.[`q_${qIndex}`] ?? '';
  }, [isSpanish, activeLang, form.questions, form.translations]);

  const hasMissingTranslation = (qIndex: number) =>
    !isSpanish && !form.translations?.[activeLang]?.[`q_${qIndex}`]?.trim();

  const saveTranslation = (qIndex: number, val: string) => {
    setForm(f => ({
      ...f,
      translations: {
        ...f.translations,
        [activeLang]: { ...(f.translations[activeLang] || {}), [`q_${qIndex}`]: val },
      },
    }));
  };

  const TypeBadge = () => form.surveyType ? (
    <span className={`preview-type-badge ${form.surveyType}`}>
      {form.surveyType === 'general' ? t('admin.builderPreviewGeneral') : t('admin.builderPreviewPersonalized')}
    </span>
  ) : null;

  const SurveyHeader = () => (
    <div className="bld-preview-top">
      <div className="pvx-edit-hint" style={{ marginBottom: 10 }}>
        <i className="fas fa-pencil-alt" /> {t('admin.builderPreviewClickToEdit')}
      </div>
      <EditableSpan
        tag="h4"
        value={form.name}
        onChange={v => setForm(f => ({ ...f, name: v }))}
        className="bld-preview-name"
      />
      <EditableSpan
        tag="p"
        value={form.description}
        onChange={v => setForm(f => ({ ...f, description: v }))}
        className="bld-preview-desc"
      />
      <TypeBadge />
      {form.anonymous && (
        <span className="bld-preview-pill anon" style={{ marginLeft: 6 }}>
          <i className="fas fa-user-secret" /> {t('admin.builderPublishAnonymousVal')}
        </span>
      )}
    </div>
  );

  if (!hasData) {
    return (
      <div className="preview-empty" style={{ paddingTop: 40 }}>
        <img src="/assets/persona-asomada.svg" alt="" style={{ width: 80, opacity: 0.5, marginBottom: 16 }} />
        <p>{t('admin.builderPreviewCompleteStep1')}</p>
      </div>
    );
  }

  if (!hasQuestions) {
    return (
      <div className="bld-preview-survey">
        <SurveyHeader />
        <PreviewLangSwitcher langs={langs} activeLang={activeLang} setPreviewLang={setPreviewLang} />
        <div className="bld-preview-qs-hint">
          <i className="fas fa-question-circle" />
          {t('admin.builderPreviewNoQuestions')}
        </div>
      </div>
    );
  }

  return (
    <div className="bld-preview-survey">
      <SurveyHeader />
      <PreviewLangSwitcher langs={langs} activeLang={activeLang} setPreviewLang={setPreviewLang} />
      {visibleQuestions.map(q => {
        const realIndex = form.questions.findIndex(fq => fq.id === q.id);
        return (
          <div key={q.id} className="pvx-block">
            <PreviewQuestionHeader
              qIndex={realIndex}
              isSpanish={isSpanish}
              activeLang={activeLang}
              getQText={getQText}
              hasMissingTranslation={hasMissingTranslation}
              saveTranslation={saveTranslation}
              onOpenIconPicker={onOpenIconPicker}
              collapsedQs={collapsedQs}
              toggleQCollapse={toggleQCollapse}
            />
            {!collapsedQs.has(realIndex) && (
              q.enfoque === 'individual' && q.presentMode === 'individual'
                ? <PreviewIndividualOptions q={q} />
                : <PreviewCollectiveOptions q={q} />
            )}
          </div>
        );
      })}
    </div>
  );
}
